import { SlidingPanel } from '@common/components/SlidingPanel/SlidingPanel';
import { Box, Flex, ScrollArea, Text } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import { useSelectedCollectionOverrideHistory } from '../hooks/useSelectedCollectionOverrideHistory';
import { OverrideHistoryItem } from '../types';
import { HistoryItemCard } from './HistoryItemCard';
import { HistoryItemCardSkeleton } from './HistoryItemCardSkeleton';

interface MetadataHistoryPanelProps {
    open: boolean;
    onClose: () => void;
}

const HistoryListContent = ({
    isFetching,
    history,
}: {
    isFetching: boolean;
    history: OverrideHistoryItem[];
}) => {
    const { t } = useTranslation('metadataEditor');

    if (isFetching) {
        return (
            <Flex direction='column' gap='4'>
                {Array.from({ length: 4 }).map((_, index) => (
                    <HistoryItemCardSkeleton key={index} />
                ))}
            </Flex>
        );
    }

    if (history.length === 0) {
        return (
            <Text align='center' color='gray' mt='6' as='div'>
                {t('history.empty')}
            </Text>
        );
    }

    return (
        <Flex direction='column' gap='4'>
            {history.map((item) => {
                const uniqueKey = `${item.timestamp}-${item.author}`;
                return <HistoryItemCard key={uniqueKey} item={item} />;
            })}
        </Flex>
    );
};

export const MetadataHistoryPanel = ({
    open,
    onClose,
}: MetadataHistoryPanelProps) => {
    const { t } = useTranslation('metadataEditor');
    const { data, isFetching } = useSelectedCollectionOverrideHistory();

    const totalEdits = data?.totalEdits ?? 0;
    const history = data?.history ?? [];

    return (
        <SlidingPanel
            open={open}
            onClose={onClose}
            title={t('history.title', 'Historique des modifications')}
            subtitle={
                totalEdits > 0
                    ? t('history.totalEdits', {
                          count: totalEdits,
                          defaultValue: `${totalEdits} modification(s)`,
                      })
                    : t('history.noEdits', 'Aucune modification')
            }
            width={{ initial: '100%', sm: '450px' }}
        >
            <ScrollArea type='auto' style={{ flex: 1 }}>
                <Box p='4'>
                    <HistoryListContent
                        isFetching={isFetching}
                        history={history}
                    />
                </Box>
            </ScrollArea>
        </SlidingPanel>
    );
};
